// src/components/russia-map/RussiaMapSVG.tsx

import React, { memo } from "react";
import { russiaRegionPaths } from "@/data/russiaRegionPaths";
import {
  getIsoCode,
  regionsData,
  districtColors,
  RegionInfo,
} from "@/lib/russiaRegions";

interface RegionData {
  leaderName?: string;
  phone?: string;
  tg?: string;
  birthDate?: string;
  appointDate?: string;
  legalDate?: string;
  deptDate?: string;
  volunteers?: number;
  studentTeams?: number;
  localDepts?: number;
  humanMission?: number;
  training?: number;
  emergency?: number;
  certified?: number;
  photoUrl?: string;
}

interface RussiaMapSVGProps {
  handleMouseEnter: (svgId: string) => void;
  handleMouseLeave: () => void;
  handleClick: (svgId: string) => void;
  selectedRegion: RegionInfo | null;
  hoveredRegion: RegionInfo | null;
  regionData: Record<string, RegionData>;
  activeCards: string[];
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  zoom: { scale: number; translateX: number; translateY: number };
}

const getRegionClass = (
  svgId: string,
  regionData: Record<string, RegionData>,
  activeCards: string[],
  selectedIsoCode: string | null,
  hoveredIsoCode: string | null
): string => {
  const isoCode = getIsoCode(svgId);
  const regionInfo = regionsData[isoCode];

  if (!regionInfo) return "fill-gray-700/30 cursor-pointer";

  let baseClass = districtColors[regionInfo.district] || "fill-gray-700/50 hover:fill-gray-600/70 cursor-pointer";

  if (activeCards.length > 0) {
    const data = regionData[isoCode] || {};
    let hasAllActivity = true;

    for (const card of activeCards) {
      let value = 0;
      switch (card) {
        case "human": value = Number(data.humanMission) || 0; break;
        case "training": value = Number(data.training) || 0; break;
        case "emergency": value = Number(data.emergency) || 0; break;
        case "sso": value = Number(data.studentTeams) || 0; break;
        case "mo": value = Number(data.localDepts) || 0; break;
        case "certified": value = Number(data.certified) || 0; break;
      }
      if (value <= 0) {
        hasAllActivity = false;
        break;
      }
    }

    if (!hasAllActivity) {
      baseClass = "fill-gray-600/40 hover:fill-gray-500/60 cursor-pointer";
    }
    // Если подходит — оставляем оригинальный цвет округа
  }

  if (isoCode === selectedIsoCode) {
    baseClass += " stroke-white stroke-6 shadow-2xl";
  }

  if (!selectedIsoCode && isoCode === hoveredIsoCode) {
    baseClass += " brightness-130";
  }

  return baseClass;
};

const RussiaMapSVG: React.FC<RussiaMapSVGProps> = memo(({
  handleMouseEnter,
  handleMouseLeave,
  handleClick,
  selectedRegion,
  hoveredRegion,
  regionData,
  activeCards,
  svgRef,
  groupRef,
  zoom,
}) => {
  const isGrabbing = zoom.scale > 1.35;

  return (
    <svg
      ref={svgRef}
      viewBox="-25 0 1400 800"
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
      style={{ cursor: isGrabbing ? "grabbing" : "grab" }}
    >
      <g
        ref={groupRef}
        transform={`translate(${zoom.translateX},${zoom.translateY}) scale(${zoom.scale})`}
        style={{ transition: "transform 0.3s ease-out" }}
      >
        {Object.entries(russiaRegionPaths).map(([svgId, dPath]) => {
          const isoCode = getIsoCode(svgId);
          if (!regionsData[isoCode]) return null;

          return (
            <path
              key={svgId}
              id={svgId}
              d={dPath}
              className={getRegionClass(
                svgId,
                regionData,
                activeCards,
                selectedRegion?.isoCode ?? null,
                hoveredRegion?.isoCode ?? null
              )}
              onMouseEnter={() => handleMouseEnter(svgId)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(svgId)}
              style={{ cursor: selectedRegion ? "default" : "pointer" }}
              aria-label={regionsData[isoCode].name}
            />
          );
        })}
      </g>
    </svg>
  );
});

RussiaMapSVG.displayName = "RussiaMapSVG";

export default RussiaMapSVG;